import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MiawChatConfig, MiawCustomerInfo } from 'src/app/model/salesforce-chat/miaw-chat.dto';
import { GoogleAnalyticsService } from './google-tag-manager.service';
import { GTMEvents } from 'src/app/model/google-tag-manager/api-status';
import { AppConstants } from 'src/app/core/constants/app.constants';
import { ChatButtonService } from './chat-button.service';

declare global {
  interface Window {
    embeddedservice_bootstrap: {
      settings: {
        language: string;
        hideChatButtonOnLoad: boolean;
        devMode: boolean;
        targetElement: HTMLElement | null;
      };
      prechatAPI?: {
        setHiddenPrechatFields: (fields: any) => void;
      };
      init: (orgId: string, deploymentName: string, siteUrl: string, options: { scrt2URL: string }) => void;
      utilAPI?: {
        launchChat: () => void;
        removeAllComponents: () => Promise<void>;
      };
      userVerificationAPI?: {
        clearSession: () => Promise<void>;
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class MiawChatService {
  private _config: MiawChatConfig;
  private _isInitialized = false;
  private _isBootstrapLoaded = false;
  private router = inject(Router);
  private chatButtonService = inject(ChatButtonService);
  private _googleAnalyticsService: GoogleAnalyticsService;
  private _badgeElement: HTMLElement | null = null;

  constructor() {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for Salesforce embedded messaging button created event
    // This event fires when the chat button DOM element is fully created and ready
    window.addEventListener('onEmbeddedMessagingButtonCreated', (event: Event) => {
      // Notify that MIAW chat button is fully ready
      this.chatButtonService.setMiawChatInitialized(true);
      this.setMiawBadgeHidden(true);
    });

    // Listen for Salesforce chat minimize event
    window.addEventListener('onEmbeddedMessagingWindowMinimized', () => {
      this.setMiawBadgeHidden(true);
    });

    // Listen for Salesforce chat maximize event
    window.addEventListener('onEmbeddedMessagingWindowMaximized', () => {
      this.setMiawBadgeHidden(false);
    });
  }

  private getBadgeElement(): HTMLElement | null {
    if (!this._badgeElement) {
      this._badgeElement = document.getElementById(AppConstants.CHAT.MIAW.BadgeId);
    }
    return this._badgeElement;
  }

  private setMiawBadgeHidden(hidden: boolean): void {
    const badge = this.getBadgeElement();
    if (!badge) {
      return;
    }
    const cls = AppConstants.CHAT.MIAW.HiddenClass;
    if (hidden && !badge.classList.contains(cls)) {
      badge.classList.add(cls);
    } else if (!hidden && badge.classList.contains(cls)) {
      badge.classList.remove(cls);
    }
    this.chatButtonService.setMiawChatVisibility(!hidden);
  }

  load(config: MiawChatConfig, googleAnalyticsService: GoogleAnalyticsService): void {
    this._isInitialized = false;
    this._config = config;
    this._googleAnalyticsService = googleAnalyticsService;
  }

  async initialize(): Promise<void> {
    if (!this._config || !this._config.Show) {
      return;
    }

    // Prevent re-initialization
    if (this._isInitialized) {
      return;
    }

    this._isInitialized = true;

    try {
      // Load the bootstrap script
      await this.loadBootstrapScript();

      // Wait for embeddedservice_bootstrap.init to be available (10 seconds timeout)
      await this.waitForEmbeddedMessagingReady();

      // Set language
      if (window.embeddedservice_bootstrap?.settings) {
        window.embeddedservice_bootstrap.settings.language = this._config.language || 'en_US';
      }

      window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;

      // Create and append miaw-chat-messaging element to body
      const targetElement = document.getElementById(AppConstants.CHAT.MIAW.BadgeId);
      if (targetElement) {
        window.embeddedservice_bootstrap.settings.targetElement = targetElement;
      }

      //window.embeddedservice_bootstrap.settings.devMode = true;

      // Set up event listener BEFORE calling init
      const readyEventPromise = this.waitForOnEmbeddedMessagingReadyEvent();

      // Initialize the embedded messaging
      window.embeddedservice_bootstrap.init(
        this._config.organizationId,
        this._config.deploymentName,
        this._config.siteUrl,
        {
          scrt2URL: this._config.scrt2URL,
        }
      );

      // Wait for the onEmbeddedMessagingReady event
      await readyEventPromise;

    } catch (err) {
      //console.error('Error loading MIAW Embedded Messaging: ', err);
    }
  }

  setCustomerDetail(customerInfo: MiawCustomerInfo): void {
    if (!this._config?.Show) {
      return;
    }

    // If already initialized, update prechat fields immediately
    if (this._isInitialized && window.embeddedservice_bootstrap?.prechatAPI) {
      // Add URL and page context
      const enrichedInfo: MiawCustomerInfo = {
        ...customerInfo,
        url: this.router.url,
        virtualPageName: this._googleAnalyticsService?.getActiveRouteTitle(),
      };

      this.setPrechatFields(enrichedInfo);
    }
  }

  show(): void {
    if (!this._isInitialized || !this._config.Show) {
      return;
    }

    this._googleAnalyticsService?.emitClickEvent(
      GTMEvents.ChatOpen,
      'Live Support - MIAW',
      undefined,
      undefined,
      undefined
    );

    this.openChat();
  }

  hide(): void {
    if (!this._isInitialized) {
      return;
    }
    const container = document.getElementById('miaw-chat-messaging') as HTMLElement | null;
    if (!container) {
      return;
    }

    const iframe = container.querySelector('#embeddedMessagingFrame') as HTMLIFrameElement | null;
    if (!iframe || !iframe.classList.contains('isMaximized')) {
      return;
    }

    const host = container.querySelector('#embedded-messaging, .embedded-messaging') as HTMLElement | null;
    const button = host?.querySelector('#embeddedMessagingConversationButton') as HTMLButtonElement | null;
    if (button) {
      button.click();
    }
  }

  openChat(): void {
    if (!this._isInitialized) {
      return;
    }

    if (window.embeddedservice_bootstrap?.utilAPI?.launchChat) {
      window.embeddedservice_bootstrap.utilAPI.launchChat();
    } else {
      //console.warn('MIAW utilAPI.launchChat not available');
    }
  }


  /**
   * Unload and clear session (equivalent to AsappUnloadAndClearSession)
   * This ends the chat and clears all session data
   */
  unloadAndClearSession(): void {
    this.clearSession().then(() => {
      this.removeAllComponents();
    });
  }

  /**
   * Clear the current session (similar to AsappUnloadAndClearSession)
   * This will end the conversation and reset the client to initial state
   * @param shouldEndSession - Whether to end the session completely (default: true)
   */
  private async clearSession(): Promise<void> {
    if (!this._isInitialized) {
      return;
    }

    if (window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession) {
      await window.embeddedservice_bootstrap.userVerificationAPI.clearSession();
    } else {
      //console.warn('MIAW clearSession not available');
    }
  }

  /**
   * Remove all MIAW components from the page
   * This should only be called after clearSession()
   */
  async removeAllComponents(): Promise<void> {
    if (!this._isInitialized) {
      return;
    }

    if (window.embeddedservice_bootstrap?.utilAPI?.removeAllComponents) {
      await window.embeddedservice_bootstrap.utilAPI.removeAllComponents();
      this._isInitialized = false;
      this._isBootstrapLoaded = false;
    } else {
      //console.warn('MIAW removeAllComponents not available');
    }
  }

  get config(): MiawChatConfig {
    return this._config;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  private setPrechatFields(customerInfo: MiawCustomerInfo): void {
    if (!window.embeddedservice_bootstrap?.prechatAPI) {
      return;
    }

    const fields: any = {};

    // Map customer info to prechat fields
    if (customerInfo.input_FirstName) fields.input_FirstName = customerInfo.input_FirstName;
    if (customerInfo.input_LastName) fields.input_LastName = customerInfo.input_LastName;
    if (customerInfo.input_Email) fields.input_Email = customerInfo.input_Email;
    if (customerInfo.input_Subject) fields.input_Subject = customerInfo.input_Subject;
    if (customerInfo.Line_of_Business) fields.Line_of_Business = customerInfo.Line_of_Business;
    if (customerInfo.Customer_Language) fields.Customer_Language = customerInfo.Customer_Language;
    if (customerInfo.Messaging_Source) fields.Messaging_Source = customerInfo.Messaging_Source;
    if (customerInfo.Phone) fields.Phone = customerInfo.Phone;
    if (customerInfo.Account_Number) fields.Account_Number = customerInfo.Account_Number;

    window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(fields);
  }

  private loadBootstrapScript(): Promise<void> {
    if (this._isBootstrapLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const scriptUrl = `${this._config.siteUrl}/assets/js/bootstrap.min.js`;

      // Check if already loaded
      if (document.querySelector(`script[src="${scriptUrl}"]`)) {
        this._isBootstrapLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = scriptUrl;
      script.onload = () => {
        this._isBootstrapLoaded = true;
        resolve();
      };
      script.onerror = (e) => reject(e);
      document.head.appendChild(script);
    });
  }

  private waitForEmbeddedMessagingReady(timeoutMs = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      let resolved = false;
      let pollInterval: any;
      let timeout: any;

      const finalize = (source: string, isTimeout = false) => {
        if (!resolved) {
          resolved = true;

          if (pollInterval) clearInterval(pollInterval);
          if (timeout) clearTimeout(timeout);

          if (isTimeout) {
            //console.error('MIAW bootstrap init not available after timeout');
            reject(new Error('Timeout waiting for embeddedservice_bootstrap.init'));
          } else {
            //console.log(`MIAW bootstrap ready via ${source}`);
            resolve();
          }
        }
      };

      // Polling approach: check if embeddedservice_bootstrap.init is available
      pollInterval = setInterval(() => {
        if (window.embeddedservice_bootstrap
            && typeof window.embeddedservice_bootstrap.init === 'function') {
          finalize('polling');
        }
      }, 100);

      // Timeout fallback
      timeout = setTimeout(() => {
        finalize('timeout', true);
      }, timeoutMs);
    });
  }

  private waitForOnEmbeddedMessagingReadyEvent(timeoutMs = 15000): Promise<void> {
    const ON_EMBEDDED_MESSAGING_READY_EVENT_NAME = 'onEmbeddedMessagingReady';
    const ON_EMBEDDED_MESSAGING_BUTTON_CREATED_EVENT_NAME = 'onEmbeddedMessagingButtonCreated';

    return new Promise((resolve) => {
      let resolved = false;
      let pollInterval: any;
      let timeout: any;

      const finalize = (source: string) => {
        if (!resolved) {
          resolved = true;
          //console.log(`MIAW ready event received via ${source}`);

          if (pollInterval) clearInterval(pollInterval);
          if (timeout) clearTimeout(timeout);

          // Remove event listeners
          window.removeEventListener(ON_EMBEDDED_MESSAGING_READY_EVENT_NAME, readyEventHandler);
          window.removeEventListener(ON_EMBEDDED_MESSAGING_BUTTON_CREATED_EVENT_NAME, buttonCreatedEventHandler);

          // Give extra time for APIs (including prechatAPI) to be fully ready
          setTimeout(() => resolve(), 500);
        }
      };

      // Event listener for onEmbeddedMessagingReady
      const readyEventHandler = () => {
        finalize('ON_EMBEDDED_MESSAGING_READY_EVENT');
      };

      // Event listener for onEmbeddedMessagingButtonCreated
      // This event fires when the chat button is created and can be an alternative signal
      const buttonCreatedEventHandler = () => {
        finalize('ON_EMBEDDED_MESSAGING_BUTTON_CREATED_EVENT');
      };

      window.addEventListener(ON_EMBEDDED_MESSAGING_READY_EVENT_NAME, readyEventHandler, { once: true });
      window.addEventListener(ON_EMBEDDED_MESSAGING_BUTTON_CREATED_EVENT_NAME, buttonCreatedEventHandler, { once: true });

      // Polling approach: check if prechatAPI is available (indicates init completed)
      pollInterval = setInterval(() => {
        if (window.embeddedservice_bootstrap?.prechatAPI
            && typeof window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields === 'function') {
          finalize('polling with prechatAPI');
        }
      }, 100);

      // Timeout fallback
      timeout = setTimeout(() => {
        //console.warn('MIAW ready event timeout - proceeding anyway');
        finalize('timeout');
      }, timeoutMs);
    });
  }
}
